import { useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import {
    GetQueueStats,
    GetQueueFailed,
    RetryFailedJobs,
    CleanCompletedJobs,
    CleanFailedJobs,
    BackupQueue,
    ClearQueue,
    PauseQueue,
    ResumeQueue,
} from '../../../services/queueService'

// Queue state has no socket event, so the screens poll their counts. Slow on
// purpose: these are background jobs, not a live feed.
export const POLL_MS = 45000

export const showToast = (icon, title) => {
    Swal.fire({
        customClass: 'swal-wide',
        icon,
        title,
        showConfirmButton: false,
        timer: 2000,
        toast: true,
        position: 'top-right',
    })
}

export const confirmAction = ({ title, text, confirmText, icon = 'warning', confirmColor = '#403fad' }) => {
    return Swal.fire({
        customClass: 'swal-wide',
        title,
        text,
        icon,
        showCancelButton: true,
        confirmButtonColor: confirmColor,
        cancelButtonColor: '#f1388b',
        confirmButtonText: confirmText,
    })
}

/**
 * Every queue screen talks to the same nine admin endpoints; only the alias and
 * the wording differ. This owns the stats/failed state and all nine actions, so
 * a queue component is left with just its own columns and job view.
 *
 * @param queue    alias sent to the API ('zatca' | 'orders' | 'erp' | 'deduct-inventory-stock')
 * @param pollMs   poll interval for stats; 0 disables polling
 * @param notes    per-queue sentences appended to the confirm dialogs
 */
const useQueue = (queue, { pollMs = 0, notes = {} } = {}) => {

    const [stats, setStats] = useState(null)
    const [statsLoader, setStatsLoader] = useState(false)
    const [statsUpdatedAt, setStatsUpdatedAt] = useState(null)

    const [failedLimit, setFailedLimit] = useState(20)
    const [failedJobs, setFailedJobs] = useState([])
    const [failedLoader, setFailedLoader] = useState(false)

    const [refreshFlag, setRefreshFlag] = useState(false)
    const [selectedIds, setSelectedIds] = useState([])

    const fetchStats = (silent = false) => {
        if (!silent) setStatsLoader(true)
        GetQueueStats(queue).then(data => {
            const body = data?.data
            const resp = body?.response || body?.data
            setStats(resp?.counts || resp || null)
            setStatsUpdatedAt(new Date())
            setStatsLoader(false)
        }).catch(error => {
            console.log("queue stats error ====> ", error)
            setStats(null)
            setStatsLoader(false)
        })
    }

    const fetchFailed = () => {
        setFailedLoader(true)
        GetQueueFailed(queue, failedLimit).then(data => {
            const body = data?.data
            const resp = body?.response || body?.data
            const rows = Array.isArray(resp?.rows) ? resp.rows
                : Array.isArray(resp) ? resp
                    : []
            setFailedJobs(rows)
            setSelectedIds([])
            setFailedLoader(false)
        }).catch(error => {
            console.log("queue failed jobs error ====> ", error)
            setFailedJobs([])
            setFailedLoader(false)
        })
    }

    useEffect(() => {
        fetchStats()
        fetchFailed()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queue, refreshFlag])

    // Queue state has no socket event, so a screen that wants live counts polls.
    // Silent: a background refresh should not flash a loader over the tiles.
    useEffect(() => {
        if (!pollMs) return
        const timer = setInterval(() => fetchStats(true), pollMs)
        return () => clearInterval(timer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queue, pollMs])

    const refresh = () => setRefreshFlag(prev => !prev)

    // ---- Selection (retry a few jobs instead of the whole failed list) ------
    const isSelected = (id) => selectedIds.includes(String(id))

    const toggleJob = (id) => setSelectedIds(prev => (
        prev.includes(String(id)) ? prev.filter(item => item !== String(id)) : [...prev, String(id)]
    ))

    const allSelected = failedJobs.length > 0 && selectedIds.length === failedJobs.length

    const toggleAll = () => setSelectedIds(allSelected ? [] : failedJobs.map(job => String(job.id)))

    const handleSuccess = (message) => {
        showToast('success', message)
        refresh()
    }

    const handleError = (error, fallback) => {
        console.log("queue action error ====> ", error)
        showToast('error', error?.response?.data?.message || fallback)
    }

    const handleRetryFailed = () => {
        confirmAction({
            title: 'Retry failed jobs?',
            text: `All failed jobs in "${queue}" queue will be re-queued.${notes.retry || ''}`,
            confirmText: 'Yes, retry!',
        }).then((result) => {
            if (!result.isConfirmed) return
            RetryFailedJobs({ queue }).then(data => {
                const retried = data?.data?.response?.retried ?? data?.data?.data?.retried ?? 0
                handleSuccess(`${retried} job(s) re-queued`)
            }).catch(err => handleError(err, 'Failed to retry jobs'))
        })
    }

    const handleRetrySelected = () => {
        if (!selectedIds.length) return
        confirmAction({
            title: `Retry ${selectedIds.length} selected job(s)?`,
            text: `Only the selected job(s) in "${queue}" queue will be re-queued.${notes.retry || ''}`,
            confirmText: 'Yes, retry!',
        }).then((result) => {
            if (!result.isConfirmed) return
            RetryFailedJobs({ queue, job_ids: JSON.stringify(selectedIds) }).then(data => {
                const retried = data?.data?.response?.retried ?? data?.data?.data?.retried ?? 0
                handleSuccess(`${retried} job(s) re-queued`)
            }).catch(err => handleError(err, 'Failed to retry jobs'))
        })
    }

    const handleCleanCompleted = () => {
        confirmAction({
            title: 'Clean completed jobs?',
            text: `All completed jobs in "${queue}" queue will be removed.`,
            confirmText: 'Yes, clean!',
        }).then((result) => {
            if (!result.isConfirmed) return
            CleanCompletedJobs({ queue }).then(data => {
                const removed = data?.data?.response?.removed ?? data?.data?.data?.removed ?? 0
                handleSuccess(`${removed} completed job(s) removed`)
            }).catch(err => handleError(err, 'Failed to clean completed jobs'))
        })
    }

    const handleCleanFailed = () => {
        confirmAction({
            title: 'Clean failed jobs?',
            text: `All failed jobs in "${queue}" queue will be removed.${notes.destructive || ''}`,
            confirmText: 'Yes, clean!',
        }).then((result) => {
            if (!result.isConfirmed) return
            CleanFailedJobs({ queue }).then(data => {
                const removed = data?.data?.response?.removed ?? data?.data?.data?.removed ?? 0
                handleSuccess(`${removed} failed job(s) removed`)
            }).catch(err => handleError(err, 'Failed to clean failed jobs'))
        })
    }

    const handleBackup = () => {
        confirmAction({
            title: 'Backup queue?',
            text: `Create a backup of "${queue}" queue.${notes.backup || ''}`,
            confirmText: 'Yes, backup!',
            icon: 'question',
        }).then((result) => {
            if (!result.isConfirmed) return
            BackupQueue({ queue }).then(data => {
                const resp = data?.data?.response || data?.data?.data || {}
                handleSuccess(`Backup created: ${resp.file || ''} (${resp.count || 0} jobs)`)
            }).catch(err => handleError(err, 'Failed to backup queue'))
        })
    }

    const handleClear = () => {
        confirmAction({
            title: 'Obliterate queue?',
            text: `This will permanently delete ALL jobs in "${queue}" queue. This cannot be undone!${notes.destructive || ''}`,
            confirmText: 'Yes, clear it!',
            confirmColor: '#d33',
        }).then((result) => {
            if (!result.isConfirmed) return
            ClearQueue({ queue, confirm: queue }).then(() => {
                handleSuccess('Queue cleared successfully')
            }).catch(err => handleError(err, 'Failed to clear queue'))
        })
    }

    const handlePause = () => {
        confirmAction({
            title: 'Pause queue?',
            text: `"${queue}" queue will stop processing new jobs.`,
            confirmText: 'Yes, pause!',
        }).then((result) => {
            if (!result.isConfirmed) return
            PauseQueue({ queue }).then(() => {
                handleSuccess('Queue paused')
            }).catch(err => handleError(err, 'Failed to pause queue'))
        })
    }

    const handleResume = () => {
        confirmAction({
            title: 'Resume queue?',
            text: `"${queue}" queue will start processing jobs again.`,
            confirmText: 'Yes, resume!',
            icon: 'question',
        }).then((result) => {
            if (!result.isConfirmed) return
            ResumeQueue({ queue }).then(() => {
                handleSuccess('Queue resumed')
            }).catch(err => handleError(err, 'Failed to resume queue'))
        })
    }

    return {
        queue,
        stats, statsLoader, statsUpdatedAt,
        failedJobs, failedLoader,
        failedLimit, setFailedLimit,
        refresh, fetchStats, fetchFailed,
        selectedIds, isSelected, toggleJob, toggleAll, allSelected,
        handleRetryFailed,
        handleRetrySelected,
        handleCleanCompleted,
        handleCleanFailed,
        handleBackup,
        handleClear,
        handlePause,
        handleResume,
    }
}

export default useQueue
